import { z } from 'zod';

/**
 * The chat socket protocol, shared by the backend handlers and the frontend.
 *
 * Requests are Socket.IO acknowledgements: the client emits a request with a
 * callback, and the server answers it exactly once with a `ChatAck` - the reply,
 * or why there is none. A failure (an invalid payload, a refusal, a server
 * error) travels in that answer and never as a socket `error`, so it reaches
 * the chat that asked, not the room connection. `ChatRequests` maps each
 * request event to its payload and reply.
 *
 * Events are only for what the server pushes unasked: `chat:initial_state`,
 * `chat:message` and `chat:activity`.
 *
 * Request payloads are Zod schemas: the server parses every one before it
 * touches the database. Replies and pushes are plain types: the server builds
 * them, so there is nothing to validate, only a shape both sides agree on.
 */

/**
 * Longest message the chat accepts, in UTF-16 code units (JS string length),
 * measured after trimming. `messages.content` is an unbounded TEXT column, so
 * this is a product limit rather than a storage one: the one the message input
 * has always shown, now enforced by the server as well.
 */
export const CHAT_MESSAGE_MAX_LENGTH = 2000;

/** Most messages a single `chat:get_messages` page may ask for. */
export const CHAT_MESSAGES_PAGE_MAX = 100;

// Channel ids, like message and user ids, are Postgres uuids
const ChannelIdSchema = z.string().uuid('Invalid channel ID');

// =============================================================================
// Requests (client to server, answered by an acknowledgement)
// =============================================================================

/**
 * `chat:send_message`: answered with the saved message. The sender pairs the
 * reply with its optimistic copy itself, so no client id is sent; any extra
 * field is stripped by the parse.
 */
export const ChatSendMessageRequestSchema = z.object({
  channelId: ChannelIdSchema,
  content: z
    .string()
    .trim()
    .min(1, 'Message cannot be empty')
    .max(CHAT_MESSAGE_MAX_LENGTH, `Message cannot be longer than ${CHAT_MESSAGE_MAX_LENGTH} characters`)
});

/** `chat:join_channel`: start viewing a channel; answered with its view. */
export const ChatJoinChannelRequestSchema = z.object({
  channelId: ChannelIdSchema
});

/** `chat:leave_channel`: stop viewing a channel (marks it read). Never refused. */
export const ChatLeaveChannelRequestSchema = z.object({
  channelId: ChannelIdSchema
});

/** `chat:start_dm`: open (creating if needed) a DM and start viewing it. */
export const ChatStartDmRequestSchema = z.object({
  targetUserId: z.string().uuid('Invalid user ID')
});

/**
 * `chat:get_messages`: a page of history older than the message `before`, or
 * the latest page without it. The server defaults `limit` when it is absent.
 */
export const ChatGetMessagesRequestSchema = z.object({
  channelId: ChannelIdSchema,
  before: z.string().uuid('Invalid message ID').optional(),
  limit: z.number().int().min(1).max(CHAT_MESSAGES_PAGE_MAX).optional()
});

export type ChatSendMessageRequest = z.infer<typeof ChatSendMessageRequestSchema>;
export type ChatJoinChannelRequest = z.infer<typeof ChatJoinChannelRequestSchema>;
export type ChatLeaveChannelRequest = z.infer<typeof ChatLeaveChannelRequestSchema>;
export type ChatStartDmRequest = z.infer<typeof ChatStartDmRequestSchema>;
export type ChatGetMessagesRequest = z.infer<typeof ChatGetMessagesRequestSchema>;

// =============================================================================
// Shapes the server sends
// =============================================================================

export type ChatChannelType = 'public' | 'dm';

/** A user as the chat shows one: the other side of a DM. */
export interface ChatUserSummary {
  id: string;
  username: string;
  avatar: string;
}

export interface ChatChannel {
  id: string;
  /** Stored name: `general` for a room's public channel, `dm-<a>-<b>` for a DM. */
  name: string;
  /** What to show: the channel name, or the other participant's username for a DM. */
  displayName: string;
  type: ChatChannelType;
  /** DM only: the other participant's id; null for a public channel. */
  dmUserId: string | null;
  /** DM only: the other participant; null for a public channel or an unknown user. */
  dmUser: ChatUserSummary | null;
}

/** A saved message. `createdAt` is an ISO 8601 timestamp. */
export interface ChatMessage {
  id: string;
  userId: string;
  username: string;
  avatar: string;
  content: string;
  createdAt: string;
}

/** A page of a channel's history, oldest message first. */
export interface ChatHistoryPage {
  messages: ChatMessage[];
  /** Whether older messages exist before this page. */
  hasMore: boolean;
  /** The page's first message, to pass as `before` for the next page; absent when it is empty. */
  oldestMessageId?: string;
}

/** A channel as the member opening it sees it: its latest history and their read marker. */
export interface ChatChannelView extends ChatHistoryPage {
  channelId: string;
  /**
   * When the member last read the channel (ISO 8601), read *before* this
   * request marked it read. Messages from others created after it are the new
   * ones: the client draws its "New Messages" divider before the first.
   */
  lastRead: string;
}

// =============================================================================
// Replies
// =============================================================================

/**
 * Why a request has no reply. `CHAT_FORBIDDEN` is the one answer to every
 * refusal, whatever its reason, so it never tells a missing channel or user
 * from a forbidden one (#116).
 */
export type ChatErrorCode = 'UNAUTHORIZED' | 'INVALID_REQUEST' | 'CHAT_FORBIDDEN' | 'CHAT_ERROR';

export interface ChatError {
  code: ChatErrorCode;
  message: string;
}

/** The acknowledgement a request is answered with, exactly once. */
export type ChatAck<T> = { ok: true; data: T } | { ok: false; error: ChatError };

/** `chat:send_message`: the message as saved. Only the sender gets it this way. */
export type ChatSendMessageReply = ChatMessage;

/** `chat:join_channel`: the channel's view. */
export type ChatJoinChannelReply = ChatChannelView;

/** `chat:start_dm`: the DM, and its view. */
export interface ChatStartDmReply extends ChatChannelView {
  channel: ChatChannel;
}

/** `chat:get_messages`: the page asked for. */
export interface ChatGetMessagesReply extends ChatHistoryPage {
  channelId: string;
}

/** `chat:leave_channel`: nothing to say; a client may leave without waiting for it. */
export type ChatLeaveChannelReply = null;

/** Every chat request: its event, what it carries and what it is answered with. */
export interface ChatRequests {
  'chat:send_message': { request: ChatSendMessageRequest; reply: ChatSendMessageReply };
  'chat:join_channel': { request: ChatJoinChannelRequest; reply: ChatJoinChannelReply };
  'chat:leave_channel': { request: ChatLeaveChannelRequest; reply: ChatLeaveChannelReply };
  'chat:start_dm': { request: ChatStartDmRequest; reply: ChatStartDmReply };
  'chat:get_messages': { request: ChatGetMessagesRequest; reply: ChatGetMessagesReply };
}

export type ChatRequestEvent = keyof ChatRequests;

// =============================================================================
// Pushes (server to client, unasked)
// =============================================================================

/**
 * `chat:initial_state`: the channel list and the unread counts, sent after
 * every island join. The only full recount: from then on `chat:activity` keeps
 * the counts, and the client sums them.
 */
export interface ChatInitialStatePayload {
  channels: ChatChannel[];
  /** Unread messages per channel id; a channel with none may be missing. */
  unreadCounts: Record<string, number>;
}

/** `chat:message`: a new message, to the sockets viewing the channel other than the sender's. */
export interface ChatMessageEventPayload {
  channelId: string;
  message: ChatMessage;
}

/**
 * `chat:activity`: a channel has a new message. Sent to every socket on the
 * island whose user may read the channel - for a DM only the other
 * participant - except the sender's. It carries no content, only the channel
 * as the recipient sees it, so a DM they have not seen yet can be listed, and
 * whether the message mentions the recipient, for the notification sound.
 */
export interface ChatActivityPayload {
  channel: ChatChannel;
  /** Whether the message @-mentions the recipient (`@username`, as the chat highlights it). */
  mentionsYou: boolean;
}
